import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { transformDataHof } from "app/components/React/TopSites/DomainsTable/transformData";
import type {
    ICategoryLeadersResponse,
    ILeaderRow,
    ITopMovers,
} from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/websiteRankings.types";
import { DefaultFetchService } from "app/services/fetchService";

import type { ITableFiltersState } from "./getRequestParams";
import { getRequestParams } from "./getRequestParams";

export const TABLE_ENDPOINT = "/api/Market/Leaders/Table";
export const EXCEL_ENDPOINT = "api/Market/Leaders/Excel";
export const BONUS_COUNTRY_TABLE_ENDPOINT = "/widgetApi/TopSitesNew/TopSites/Table";
export const BONUS_COUNTRY_EXCEL_ENDPOINT = "widgetApi/TopSitesNew/TopSites/Excel";

const EMPTY_TOP_MOVERS: ITopMovers = { climbers: [], fallers: [], newEntrants: [] };

// Derived rather than imported from `app/services/segments/**`: this file is in the
// `app-shared` boundary zone, which `.fallowrc.json` does not allow to depend on the
// `segments` zone. Mirrors the same derivation in the adjacent transformData.ts.
type SegmentsModule = Parameters<typeof transformDataHof>[0];

export const getIsBonusCountry = (country: string): boolean => {
    const availableCountries = swSettings.components.Home.resources.Countries;
    return !availableCountries.includes(Number(country));
};

const hasAnyMover = (movers?: ITopMovers): movers is ITopMovers =>
    Boolean(movers?.climbers.length || movers?.fallers.length || movers?.newEntrants.length);

// Movers describe the whole industry, not the filtered/paged result set: a request only
// qualifies as "unfiltered" when it carries neither the search `filter` nor a `funcFlag`
// (website-type) param.
export const isUnfilteredRequest = (requestParams: Record<string, unknown>): boolean =>
    !("filter" in requestParams) && !("funcFlag" in requestParams);

interface IUseIndustryLeadersTableArgs {
    params: Record<string, unknown>;
    filters: ITableFiltersState;
    page: number;
}

export const useIndustryLeadersTable = ({ params, filters, page }: IUseIndustryLeadersTableArgs) => {
    const isBonusCountry = getIsBonusCountry(params.country as string);
    const tableEndpoint = isBonusCountry ? BONUS_COUNTRY_TABLE_ENDPOINT : TABLE_ENDPOINT;
    const excelEndpoint = isBonusCountry ? BONUS_COUNTRY_EXCEL_ENDPOINT : EXCEL_ENDPOINT;

    const segmentsModule: SegmentsModule = useSelector((state) => state.segmentsModule);
    // `select` only skips re-running when its reference is stable across renders.
    const transformData = useMemo(() => transformDataHof(segmentsModule), [segmentsModule]);

    // Excluding `page` keeps this object usable as-is for the Excel download URL.
    const requestParams = useMemo(() => getRequestParams(params, filters), [params, filters]);

    const { data, error, isPending, isLoading, isFetching, isError } = useQuery({
        queryKey: [tableEndpoint, requestParams, page],
        queryFn: () =>
            DefaultFetchService.getInstance().get<ICategoryLeadersResponse>(tableEndpoint, {
                ...requestParams,
                page,
            }),
        select: transformData,
        // Every one of `search`/`funcFlag`/`sort`/`page` changes the queryKey, which would
        // otherwise drop back to `isPending` on each. The table itself still shows a skeleton
        // while refetching (see `isLoading` below) — what this preserves across a refetch is
        // `totalCount` and the movers latch, so neither the header count nor the strip blanks.
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        if (isError) {
            Sentry.error("ERROR fetching table data", error);
        }
    }, [isError, error]);

    // Movers describe the whole dataset rather than the current page, and the bonus-country
    // endpoint omits them entirely — so hold the last non-empty set instead of blanking the
    // strip on a page change. Only latch from an unfiltered request: a search or website-type
    // filter narrows the result set the movers are computed over, so a filtered response's
    // mover arrays must never overwrite the strip.
    const [latchedTopMovers, setLatchedTopMovers] = useState<ITopMovers>(EMPTY_TOP_MOVERS);
    const responseTopMovers = data?.topMovers;
    const freshTopMovers =
        isUnfilteredRequest(requestParams) && hasAnyMover(responseTopMovers) ? responseTopMovers : undefined;
    useEffect(() => {
        if (freshTopMovers) {
            setLatchedTopMovers(freshTopMovers);
        }
    }, [freshTopMovers]);
    // Read the fresh set directly on the render it arrives and fall back to the latch only
    // once a filter or page change stops supplying one. Without this the strip would show
    // its empty state for the frame between the response landing and the effect committing.
    const topMovers = freshTopMovers ?? latchedTopMovers;

    const rows: ILeaderRow[] = data?.records ?? [];

    return {
        rows,
        totalCount: data?.TotalCount ?? 0,
        topMovers,
        // Only the very first load should flash the movers strip loader.
        isTopMoversLoading: isPending,
        isLoading: isLoading || isFetching,
        isError,
        excelEndpoint,
        isBonusCountry,
        requestParams,
    };
};
